import { ErrorResponse } from '@models/error';
import { getAccessToken, getRefreshToken, reissueAndStoreToken } from '@utils/authUtils';
import axios, {
    AxiosError,
    AxiosInstance,
    AxiosRequestConfig,
    AxiosRequestTransformer,
    InternalAxiosRequestConfig,
} from 'axios';
import dayjs from 'dayjs';
import Cookies from 'universal-cookie';

const cookies = new Cookies(null, { path: '/' });

export interface HttpClient extends AxiosInstance {
    get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T>;
    post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>;
}

const isPlainObject = (v: unknown) => Object.prototype.toString.call(v) === '[object Object]';

const dateTransformer: AxiosRequestTransformer = (data: any) => {
    if (data instanceof Date) {
        return dayjs(data).toISOString();
    }
    if (Array.isArray(data)) {
        // @ts-ignore
        return data.map((val: any) => dateTransformer(val));
    }
    if (isPlainObject(data)) {
        return Object.fromEntries(
            // @ts-ignore
            Object.entries(data).map(([key, val]) => [key, dateTransformer(val)]),
        );
    }
    return data;
};

const instance: HttpClient = axios.create({
    baseURL: import.meta.env.VITE_API_URL,
    withCredentials: true,
    // @ts-ignore
    transformRequest: [dateTransformer].concat(axios.defaults.transformRequest),
    paramsSerializer: {
        indexes: null,
    },
});

let sessionExpired = false;

// 서버에서 세션이 무효화된 경우(어드민 탈퇴 처리, 리프레시 토큰 폐기 등).
// 스택의 인증 화면들과 redux의 주문/장바구니 상태가 그대로 남지 않도록
// 진입 경로로 되돌려 앱 상태를 통째로 비운다.
function expireSession() {
    cookies.remove('accessToken', { path: '/' });
    cookies.remove('refreshToken', { path: '/' });
    // 동시에 실패한 요청들이 각각 이동을 일으키지 않도록 한 번만 수행한다.
    if (sessionExpired) {
        return;
    }
    sessionExpired = true;
    window.location.href = '/';
}

function getProjectId() {
    const projectId = cookies.get('projectId');
    if (projectId) {
        return projectId;
    }
    const params = new URLSearchParams(window.location.search);
    return params.get('projectId');
}

// 로그인 요청은 토큰이 필요 없다. 남아 있던 옛 refreshToken 때문에 재발급이 끼어들면
// 로그인 흐름이 먼저 세션 만료 처리로 끌려가므로 재발급 대상에서 제외한다.
const AUTH_FREE_PATHS = ['/oauth/sign-in', '/auth/signin'];

const isAuthFreePath = (url?: string) => AUTH_FREE_PATHS.some((path) => url?.includes(path));

// _retry: 재시도한 요청이 다시 401을 받아도 무한 루프에 빠지지 않게 하는 요청 단위 표시.
// _accessToken: 이 요청이 실제로 달고 나간 액세스 토큰. 401을 받은 뒤 "내가 보낸 토큰이
// 아직 최신인지"를 판별해, 다른 요청이 이미 끝낸 재발급을 다시 하지 않도록 쓴다.
type RetryableConfig = InternalAxiosRequestConfig & {
    _retry?: boolean;
    _accessToken?: string;
};

function authorize(config: RetryableConfig, accessToken: string) {
    config.headers.Authorization = `Bearer ${accessToken}`;
    config._accessToken = accessToken;
}

instance.interceptors.request.use(async (config: RetryableConfig) => {
    config.headers['Waldlust-Project-ID'] = getProjectId();
    const lang = cookies.get('lang');
    if (lang) {
        config.headers['Accept-Language'] = lang;
    }
    let accessToken = getAccessToken();
    // accessToken 쿠키가 refreshToken보다 먼저 만료되므로, 토큰 없이 보내 401을 받고
    // 되돌아오는 대신 여기서 먼저 재발급한다. reissueAndStoreToken은 single-flight라
    // 동시에 나가는 요청들은 같은 재발급 하나를 함께 기다린다.
    if (!accessToken && !isAuthFreePath(config.url) && getRefreshToken()) {
        try {
            accessToken = (await reissueAndStoreToken()).token;
        } catch (e) {
            console.error('리프레시 실패', e);
            expireSession();
            throw e;
        }
    }
    if (accessToken) {
        authorize(config, accessToken);
    }
    return config;
});

instance.interceptors.response.use(
    (res) => res.data,
    async (error: AxiosError<ErrorResponse>) => {
        // 요청 인터셉터에서 던진 오류도 같은 체인을 타고 이 핸들러로 들어온다.
        // 응답에서 비롯된 오류가 아니면 이미 기록되었으므로 그대로 넘긴다.
        if (!axios.isAxiosError(error)) {
            return Promise.reject(error);
        }
        const originalConfig = error.config as RetryableConfig | undefined;
        if (error.response?.status === 401
            // 프로젝트에 진입한 경우에만 재발급 후 재시도한다.
            && cookies.get('projectId')
            && originalConfig
            && !isAuthFreePath(originalConfig.url)
            && !originalConfig._retry
            && getRefreshToken()
        ) {
            originalConfig._retry = true;
            const currentToken = getAccessToken();
            // 이 요청이 나간 뒤 다른 요청이 이미 재발급을 끝냈다면, 또 발급받지 않고
            // 새 토큰으로 재시도만 한다. (서버가 refreshToken을 회전시키므로 불필요한
            // 재발급은 그만큼 연속 회전을 일으킨다)
            if (currentToken && originalConfig._accessToken !== currentToken) {
                authorize(originalConfig, currentToken);
                return instance(originalConfig);
            }
            try {
                const { token } = await reissueAndStoreToken();
                authorize(originalConfig, token);
                return instance(originalConfig);
            } catch (e) {
                console.error(e);
                expireSession();
            }
        }
        return Promise.reject(error);
    },
);

export default instance;
